import React from 'react';
import {
  ADMIN_MESSAGE_FORMATS,
  isInvoiceResubmissionRequestedFormat,
  isStageSeatingShareAnnouncementFormat,
} from '../../../../constants/messageFormats';
import SystemMessageCard from './SystemMessageCard';

export const renderSystemChrome = (
  children,
  { title = '', showIcon = false, iconClass = 'fa-bullhorn', badge = '', cardClass = '' } = {},
) => (
  <SystemMessageCard
    title={title}
    showIcon={showIcon}
    iconClass={iconClass}
    badge={badge}
    className={cardClass}
  >
    {children}
  </SystemMessageCard>
);

export const buildSystemTextDescriptor = ({
  title = '',
  content = null,
  bodyExtra = null,
  extra = null,
  showIcon = false,
  iconClass = 'fa-bullhorn',
  badge = '',
  cardClass = '',
  emptyText = '本文がありません。',
  forceMuted = false,
}) => ({
  kind: 'text',
  title,
  content,
  bodyExtra,
  extra,
  showIcon,
  iconClass,
  badge,
  cardClass,
  emptyText,
  forceMuted,
});

export const buildSystemRichDescriptor = ({
  title = '',
  content = null,
  showIcon = false,
  iconClass = 'fa-bullhorn',
  badge = '',
  cardClass = '',
}) => ({
  kind: 'rich',
  title,
  content,
  showIcon,
  iconClass,
  badge,
  cardClass,
});

export const renderSystemDescriptorCard = (descriptor = {}) => {
  const {
    kind = 'text',
    title = '',
    content = null,
    bodyExtra = null,
    extra = null,
    showIcon = false,
    iconClass = 'fa-bullhorn',
    badge = '',
    cardClass = '',
    emptyText = '本文がありません。',
    forceMuted = false,
  } = descriptor;

  if (kind === 'rich') {
    return renderSystemChrome(content, {
      title,
      showIcon,
      iconClass,
      badge,
      cardClass,
    });
  }

  const hasStringContent = typeof content === 'string' ? content.trim().length > 0 : null;
  const hasContent = hasStringContent != null ? hasStringContent : Boolean(content);
  const textClassName = `system-message-card__text${
    !hasContent || forceMuted ? ' system-message-card__text--muted' : ''
  }`;

  return renderSystemChrome(
    <>
      <div className={textClassName}>{hasContent ? content : emptyText}</div>
      {bodyExtra}
      {extra}
    </>,
    {
      title,
      showIcon,
      iconClass,
      badge,
      cardClass,
    },
  );
};

export const renderSystemTextCard = (props = {}) =>
  renderSystemDescriptorCard(buildSystemTextDescriptor(props));

export const getSystemToneClass = (message) => {
  const format = message?.message_format;
  if (format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) return 'system-message-card--reminder';
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD) return 'system-message-card--offer';
  if (
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED
  ) {
    return 'system-message-card--contract';
  }
  if (
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED ||
    isInvoiceResubmissionRequestedFormat(format)
  ) {
    return 'system-message-card--alert';
  }
  if (isStageSeatingShareAnnouncementFormat(format)) {
    return 'system-message-card--stage-seating';
  }
  return '';
};

export const getSystemAvatarToneClass = (message) => {
  const cardTone = getSystemToneClass(message);
  if (cardTone === 'system-message-card--offer') return 'announce-tone--offer';
  if (cardTone === 'system-message-card--contract') return 'announce-tone--contract';
  if (cardTone === 'system-message-card--alert') return 'announce-tone--alert';
  if (cardTone === 'system-message-card--stage-seating') return 'announce-tone--stage-seating';
  if (cardTone === 'system-message-card--reminder') return 'announce-tone--reminder';
  return 'announce-tone--info';
};

export const getSystemAvatarIconClass = (message) => {
  const format = message?.message_format;
  if (format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) return 'fa-bell';
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD) return 'fa-calendar-plus';
  if (
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED
  ) {
    return 'fa-file-signature';
  }
  if (
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE ||
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED ||
    isInvoiceResubmissionRequestedFormat(format)
  ) {
    return 'fa-triangle-exclamation';
  }
  if (isStageSeatingShareAnnouncementFormat(format)) return 'fa-chair';
  if (format === 'system_notice') return 'fa-circle-info';
  return 'fa-bullhorn';
};

export const getReminderHeader = (message, kindOverride = '') => {
  const raw = message?.content || '';
  const matched = raw.match(/kind:(prevday|today1h|today8am)/);
  const kind = kindOverride || message?.reminder_kind || (matched ? matched[1] : null);
  if (kind === 'prevday') return '明日のスケジュール';
  if (kind === 'today1h') return '今日のスケジュール（開始1時間前）';
  if (kind === 'today8am') return '今日のスケジュール';
  return 'スケジュールのお知らせ';
};

export const sanitizeReminderContent = (raw) => {
  const lines = String(raw || '').split(/\r?\n/);
  const filtered = lines
    .map((line) => line.replace(/^【前日リマインド】\s*/u, '').replace(/^【当日リマインド】\s*/u, ''))
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) return false;
      if (/^\[schedule_id:.*\]\[kind:.*\]$/i.test(trimmed)) return false;
      if (/^\[manual_event_uuid:.*\]\[artist_id:.*\]\[date:.*\]\[source:.*\]\[kind:.*\]$/i.test(trimmed)) return false;
      return true;
    });
  return filtered.join('\n');
};

const REMINDER_TAG_RE = /\[schedule_id:([0-9a-f-]+)\]\[kind:([^\]]+)\]/i;
export const parseReminderTags = (content = '') => {
  const matched = REMINDER_TAG_RE.exec(String(content));
  return {
    scheduleId: matched?.[1] || '',
    kind: matched?.[2] || '',
  };
};
